<template>
  <div class="restriction-stats grey--text text--darken-1">
    <small v-if="!stats">no customer usage recorded yet</small>
    <template v-else>
      <small :title="detail">{{ headline }}</small>
      <div v-if="detailed">
        <small>{{ detail }}</small>
      </div>
    </template>
  </div>
</template>

<style scoped>
.restriction-stats {
  line-height: 1.3;
}
</style>

<script>
import moment from "moment";

/**
 * How much a food restriction is actually used, from the weekly customer-allergy-stats job
 * (fed-jobs). `stats` is the block GET v2/restrictions/stats hangs off each restriction, and is
 * null until that job has recorded the restriction.
 *
 * The headline count is every customer who has it on their profile, ordering or not. The
 * breakdown behind it is a single week of orders - the most recent week the job recorded - so a
 * restriction can have customers and still show no orders.
 */
export default {
  name: "RestrictionStats",
  props: {
    stats: {type: Object, required: false, default: null},
    // show the week's breakdown as a second line rather than only on hover
    detailed: {type: Boolean, required: false, default: false}
  },
  methods: {
    plural(count, word) {
      return `${count} ${word}${count === 1 ? '' : 's'}`;
    },
    // weeks are stored as the monday, so say which week rather than pretending it is a day
    week(week_date) {
      return week_date ? `week of ${week_date} (${moment(week_date).fromNow()})` : 'never';
    }
  },
  computed: {
    headline() {
      // currentCustomerCount is everyone who has it on their profile, ordering or not, so the
      // last seen week carries its own count rather than being read against that one
      const {currentCustomerCount, lastUsedWeek, lastUsedCustomers} = this.stats;
      const lastSeen = lastUsedWeek
        ? `${this.week(lastUsedWeek)}, ${this.plural(lastUsedCustomers, 'customer')} ordering`
        : 'never';
      return `${this.plural(currentCustomerCount, 'customer')} · last seen ${lastSeen}`;
    },
    detail() {
      const {
        week_date, customers, detectedCustomers, detectedMealQuantity, distinctMealsImpacted,
        lastDetectedWeek, total
      } = this.stats;
      if (!week_date) {
        return 'no orders recorded yet';
      }
      const parts = [
        `week of ${week_date}: ${this.plural(customers, 'customer')} ordering, ` +
        `${detectedCustomers} with it detected ` +
        `on ${this.plural(detectedMealQuantity, 'meal')} ordered ` +
        `across ${this.plural(distinctMealsImpacted, 'menu meal')}`
      ];
      if (lastDetectedWeek !== week_date) {
        parts.push(`last detected ${this.week(lastDetectedWeek)}`);
      }
      if (total && total.weeks) {
        parts.push(`recorded over ${this.plural(total.weeks, 'week')}`);
      }
      return parts.join(' · ');
    }
  }
}
</script>
